import { formatDateValue } from "@/lib/utils";
import { FieldDataType, FormatFunction, FormatFunctionRegistry } from "@/types/dynamic-detail";
import React from "react";
import { formatPercentageFromRatio } from "./format-time";

// Fonctions de formatage pour différents types de champs
export const formatters: FormatFunctionRegistry = {
    text: (value: any) => {
        return value ? String(value) : "-";
    },

    date: (value: any) => {
        if (!value) return "-";
        try {
            const originalDate = new Date(value);
            // Créer une nouvelle date sans les composants de temps pour forcer le formatage en date seule
            const date = new Date(originalDate.getFullYear(), originalDate.getMonth(), originalDate.getDate());
            return formatDateValue(date, "long");
        } catch {
            return String(value);
        }
    },

    datetime: (value: any) => {
        if (!value) return "-";
        try {
            const date = new Date(value);
            return formatDateValue(date, "long");
        } catch {
            return String(value);
        }
    },

    boolean: (value: any) => {
        if (value === null || value === undefined) return "-";
        return value ? "Yes" : "No";
    },

    number: (value: any) => {
        if (value === null || value === undefined) return "-";
        return Number(value).toLocaleString();
    },

    percentage: (value: any) => {
        if (value === null || value === undefined) return "-";
        return formatPercentageFromRatio(Number(value));
    },

    currency: (value: any) => {
        if (value === null || value === undefined) return "-";
        return Number(value).toLocaleString("fr-FR", {
            style: "currency",
            currency: "EUR"
        });
    },

    url: (value: any) => {
        if (!value) return "-";
        return (
            <a href={String(value)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                {String(value)}
            </a>
        );
    },

    email: (value: any) => {
        if (!value) return "-";
        return (
            <a href={`mailto:${String(value)}`} className="text-blue-600 hover:text-blue-800 hover:underline">
                {String(value)}
            </a>
        );
    },

    phone: (value: any) => {
        if (!value) return "-";
        return (
            <a href={`tel:${String(value)}`} className="text-blue-600 hover:text-blue-800 hover:underline">
                {String(value)}
            </a>
        );
    }
};

/**
 * Formate une valeur selon son type de champ
 */
export function formatFieldValue(value: any, field: FieldDataType): React.ReactNode {
    // Si une fonction de formatage personnalisée est spécifiée
    if (field.format_fn && formatters[field.format_fn]) {
        return formatters[field.format_fn](value, field);
    }

    // Sinon, utiliser le formatage par défaut selon le type de champ
    const formatter = formatters[field.field_type] || formatters.text;
    return formatter(value, field);
}

/**
 * Ajoute une fonction de formatage personnalisée
 */
export function addFormatter(name: string, formatter: FormatFunction): void {
    formatters[name] = formatter;
}
